import type {
  ObservationsMapParameterDisplayGroup,
  ObservationsMapWidgetConfiguration,
  ParameterTimeSeriesConfiguration,
  ParameterTimeSeriesConfigurationBody,
} from "@/lib/api/generated/types";
import {
  backfillParameterTimeSeriesConfigurations,
  normalizeParameterTimeSeriesConfigurationsForSave,
  stripDisplayGroupParametersWithoutTimeSeries,
  type BackfillCatalogParameter,
} from "@/lib/api/parameter-time-series-configuration.utils";
import type { UnitFamilyMap } from "@/lib/parameters/parameter-units.utils";

function readRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object") return null;
  return value as Record<string, unknown>;
}

export function readObservationsMapTimeSeriesConfigurations(
  configuration: ObservationsMapWidgetConfiguration,
): ParameterTimeSeriesConfiguration[] {
  return configuration.parameter_time_series_configurations ?? [];
}

export function normalizeObservationsMapTimeSeriesConfigurations(
  configurations: ParameterTimeSeriesConfiguration[] | undefined,
): ParameterTimeSeriesConfiguration[] {
  if (!Array.isArray(configurations)) return [];
  return configurations.map((c) => {
    const ok =
      typeof c.visualization_type === "string" && c.visualization_type.trim().length > 0;
    if (ok) return c;
    return { ...c, visualization_type: "line_graph" };
  });
}

export function mergeObservationsMapTimeSeriesInitialValues<
  T extends { configuration?: unknown },
>(initialValues: T): T {
  const config = initialValues?.configuration;
  if (!config || typeof config !== "object") return initialValues;
  const typed = config as ObservationsMapWidgetConfiguration;
  if (typed.type !== "observations_map") return initialValues;

  let configuration = { ...typed };
  let changed = false;

  if (!Array.isArray(configuration.parameter_time_series_configurations)) {
    configuration = {
      ...configuration,
      parameter_time_series_configurations: [],
    };
    changed = true;
  }

  const normalized = normalizeObservationsMapTimeSeriesConfigurations(
    configuration.parameter_time_series_configurations,
  );
  const tsChanged =
    normalized.length !==
      (configuration.parameter_time_series_configurations?.length ?? 0) ||
    normalized.some(
      (c, i) =>
        c.visualization_type !==
        configuration.parameter_time_series_configurations?.[i]?.visualization_type,
    );
  if (tsChanged) {
    configuration = {
      ...configuration,
      parameter_time_series_configurations: normalized,
    };
    changed = true;
  }

  if (!changed) return initialValues;
  return {
    ...initialValues,
    configuration,
  };
}

export function normalizeObservationsMapDisplayGroups(
  groups: ObservationsMapParameterDisplayGroup[] | undefined,
): ObservationsMapParameterDisplayGroup[] {
  if (!Array.isArray(groups)) return [];
  return groups
    .map((group) => {
      const trimmed = group.label_override?.trim();
      return {
        label_override: trimmed ? trimmed : null,
        parameter_ids: [...group.parameter_ids],
      };
    })
    .filter((group) => group.parameter_ids.length > 0);
}

export function mergeObservationsMapDisplayGroupInitialValues<
  T extends { configuration?: unknown },
>(initialValues: T): T {
  const config = initialValues?.configuration;
  if (!config || typeof config !== "object") return initialValues;
  const typed = config as ObservationsMapWidgetConfiguration;
  if (typed.type !== "observations_map") return initialValues;

  if (Array.isArray(typed.parameter_display_groups)) {
    const normalized = normalizeObservationsMapDisplayGroups(
      typed.parameter_display_groups,
    );
    const changed =
      normalized.length !== typed.parameter_display_groups.length ||
      normalized.some(
        (group, index) =>
          group.label_override !== typed.parameter_display_groups[index]?.label_override ||
          group.parameter_ids.length !==
            typed.parameter_display_groups[index]?.parameter_ids.length ||
          group.parameter_ids.some(
            (id, i) => id !== typed.parameter_display_groups[index]?.parameter_ids[i],
          ),
      );
    if (!changed) return initialValues;
    return {
      ...initialValues,
      configuration: {
        ...typed,
        parameter_display_groups: normalized,
      },
    };
  }

  return {
    ...initialValues,
    configuration: {
      ...typed,
      parameter_display_groups: [],
    },
  };
}

export function backfillObservationsMapTimeSeriesConfigurations(
  configurations: ParameterTimeSeriesConfiguration[] | undefined,
  catalog: BackfillCatalogParameter[],
  families?: UnitFamilyMap,
): ParameterTimeSeriesConfiguration[] {
  return backfillParameterTimeSeriesConfigurations(configurations, catalog, families);
}

/** Normalizes observations map widget configuration on CMS save. */
export function normalizeObservationsMapWidgetFormValues<
  T extends { configuration?: unknown },
>(values: T): T {
  const config = readRecord(values.configuration);
  if (!config || config.type !== "observations_map") return values;

  const typed = config as unknown as ObservationsMapWidgetConfiguration;
  const normalizedTimeSeries = normalizeObservationsMapTimeSeriesConfigurations(
    typed.parameter_time_series_configurations,
  );
  const configuration: ObservationsMapWidgetConfiguration = {
    ...typed,
    show_all_locations: typed.show_all_locations ?? true,
    weather_stations_enabled_by_default: typed.weather_stations_enabled_by_default ?? true,
    parameter_time_series_configurations: normalizeParameterTimeSeriesConfigurationsForSave(
      normalizedTimeSeries,
    ) as unknown as ParameterTimeSeriesConfiguration[],
    parameter_display_groups: stripDisplayGroupParametersWithoutTimeSeries(
      normalizeObservationsMapDisplayGroups(typed.parameter_display_groups),
      normalizedTimeSeries,
    ),
  };

  return { ...values, configuration } as T;
}

/** Normalizes model map widget configuration on CMS save. */
export function normalizeModelMapWidgetFormValues<T extends { configuration?: unknown }>(
  values: T,
): T {
  const config = readRecord(values.configuration);
  if (!config || config.type !== "model_map") return values;

  const typed = config as {
    type: "model_map";
    parameter_time_series_configurations?: ParameterTimeSeriesConfiguration[];
    [key: string]: unknown;
  };
  const normalizedTimeSeries = normalizeObservationsMapTimeSeriesConfigurations(
    typed.parameter_time_series_configurations,
  );
  const parameterTimeSeriesBodies: ParameterTimeSeriesConfigurationBody[] =
    normalizeParameterTimeSeriesConfigurationsForSave(normalizedTimeSeries);

  return {
    ...values,
    configuration: {
      ...typed,
      parameter_time_series_configurations: parameterTimeSeriesBodies,
    },
  } as T;
}

export function mergeObservationsMapStationFilterInitialValues<
  T extends { configuration?: unknown },
>(initialValues: T): T {
  const config = initialValues?.configuration;
  if (!config || typeof config !== "object") return initialValues;
  const typed = config as ObservationsMapWidgetConfiguration;
  if (typed.type !== "observations_map") return initialValues;
  const hasShowAllLocations = typeof typed.show_all_locations === "boolean";
  const hasWeatherStationsEnabledByDefault =
    typeof typed.weather_stations_enabled_by_default === "boolean";
  if (hasShowAllLocations && hasWeatherStationsEnabledByDefault) return initialValues;

  return {
    ...initialValues,
    configuration: {
      ...typed,
      show_all_locations: hasShowAllLocations ? typed.show_all_locations : true,
      weather_stations_enabled_by_default: hasWeatherStationsEnabledByDefault
        ? typed.weather_stations_enabled_by_default
        : true,
    },
  };
}
